import * as Sentry from '@sentry/react';

export const WEAR_FEATURE_FLAG_NAME = 'IS_WEAR_ENABLED';

// Upper bound on the IS_WEAR_ENABLED lookup. Login routing waits on it, so a
// hung request must not hold the user on the login page; a timeout aborts the
// fetch and resolves to false through the catch below.
export const WEAR_FEATURE_FLAG_TIMEOUT_MS = 4000;

type OrgFeatureFlag = Utils.ArrayElement<Api.Response['listOrgFeatureFlags']>;

/**
 * @description Reads the IS_WEAR_ENABLED feature flag for an org straight from
 * Helium (`GET /orgs/{orgId}/featureFlags`, endpoint `listOrgFeatureFlags`).
 *
 * The useFeatureFlag hook cannot be used here: the login flow only learns the
 * org id and the region-specific Helium endpoint after the login request
 * resolves, inside an event handler. This calls the same endpoint the hook
 * uses, against the Helium that issued the token, with that token. The
 * endpoint returns the org's value or, when the org has none, the global
 * default.
 *
 * Any failure (network error, timeout after WEAR_FEATURE_FLAG_TIMEOUT_MS,
 * non-2xx, flag missing) returns false, so the
 * caller falls through to the normal post-login routing.
 * @param heliumEndpoint - Helium origin that issued the token
 * @param token - access token to authorize the request
 * @param orgId - org to read the flag for
 * @returns {Promise<boolean>} True only when the flag is set to true.
 */
export const getIsWearEnabled = async ({
  heliumEndpoint,
  token,
  orgId,
}: {
  heliumEndpoint: string;
  token?: string | null;
  orgId?: number | null;
}): Promise<boolean> => {
  if (!heliumEndpoint || !token || !orgId) return false;

  try {
    const response = await fetch(
      `${heliumEndpoint}/orgs/${orgId}/featureFlags`,
      {
        method: 'GET',
        headers: {
          Authorization: token,
        },
        signal: AbortSignal.timeout(WEAR_FEATURE_FLAG_TIMEOUT_MS),
      },
    );

    if (!response.ok) return false;

    const { data } = (await response.json()) as { data?: OrgFeatureFlag[] };

    const wearFlag = data?.find(
      ({ featureFlag }) => featureFlag?.name === WEAR_FEATURE_FLAG_NAME,
    );

    // Helium serializes boolean flag values as booleans; accept the stored
    // string forms too, matching Helium's own boolean coercion.
    const value: unknown = wearFlag?.value;
    return value === true || value === '1' || value === 'true';
  } catch (err) {
    Sentry.captureMessage(`${WEAR_FEATURE_FLAG_NAME} lookup failed`, {
      level: 'log',
      extra: {
        error:
          err instanceof Error ? `${err.name}: ${err.message}` : String(err),
        orgId,
      },
    });
    return false;
  }
};
